import { create } from "zustand";
import { persist } from "zustand/middleware";
import { secureTokenManager } from "../utils/secureTokenManager";
import { clearSetupChecklistStorageOnLogout } from "../utils/providerSetupChecklistStorage";
import { clearAuthSessionStorage } from "../utils/clearAuthSessionStorage";
import { secureErrorHandler } from "../utils/secureErrorHandler";
import { authAPI } from "../services/api";
import { TOKEN_STORAGE_KEYS, STORAGE_KEYS } from "../utils/constants";
import { useProviderStore } from "./providerStore";
import { setSentryUser } from "../config/sentry";
import { productAnalytics } from "../config/productAnalytics";
import { clearDashboardCompareLastOpenedStorage } from "../utils/dashboardInstrumentation";
import { offlineStorage } from "../utils/offlineStorage";
import { useCommercialEntitlementStore } from "./commercialEntitlementStore";
import { clearStashedAgencyInvitationToken } from "../utils/agencyInvitationTokenSession";
import { useCareCoordinationContextStore } from "./careCoordinationContextStore";

/** Must match persist() version option so manual cleared-state writes rehydrate correctly. */
const AUTH_PERSIST_VERSION = 1;

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name?: string; // Computed property for backward compatibility
  role: "admin" | "provider" | "provider_staff" | "patient" | "social_worker";
  is_active: boolean;
  is_verified: boolean;
}

/** Persist only non-PII identity + role; refresh email/name via GET /auth/me. */
export function toPersistedUser(user: User | null): Partial<User> | null {
  if (!user) return null;
  return {
    id: user.id,
    role: user.role,
    is_active: user.is_active,
    is_verified: user.is_verified,
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (user: User, token?: string) => void;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  updateToken: (token: string | null) => void;
  clearAuth: () => void;
  // Role-based properties
  /** Self-serve registrant UX only — not Layer 2 Owner powers */
  isProvider: boolean;
  /** Provider portal access: self-serve owner or invited staff */
  isProviderPortalUser: boolean;
  /** Alias of registrant UX; prefer this name for wizard gates */
  isProviderAccountOwner: boolean;
  isSocialWorker: boolean;
  isPatient: boolean;
  /** @deprecated Prefer ``isPlatformAdmin`` — kept for existing call sites */
  isAdmin: boolean;
  /** Platform operator only (Layer 1). Never use for org employee management. */
  isPlatformAdmin: boolean;
}

function _roleFlags(role: User["role"] | undefined) {
  const isAdmin = role === "admin";
  const isProvider = role === "provider";
  return {
    isProvider,
    isProviderPortalUser: role === "provider" || role === "provider_staff",
    isProviderAccountOwner: isProvider,
    isSocialWorker: role === "social_worker",
    isPatient: role === "patient",
    isAdmin,
    isPlatformAdmin: isAdmin,
  };
}

function _clearOrgContextStore() {
  try {
    // Lazy import — avoid circular dependency at module load
    void import("./orgContextStore").then((mod) => {
      mod.useOrgContextStore.getState().clear();
    });
  } catch {
    // ignore
  }
}

function _clearCommercialEntitlementStore() {
  useCommercialEntitlementStore.getState().clear();
}

function _clearCareCoordinationContextStore() {
  useCareCoordinationContextStore.getState().clear();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isProvider: false,
      isProviderPortalUser: false,
      isProviderAccountOwner: false,
      isSocialWorker: false,
      isPatient: false,
      isAdmin: false,
      isPlatformAdmin: false,
      login: (user: User, token?: string) => {
        // Never carry a previous authenticated subject's paid state into a new session.
        _clearCommercialEntitlementStore();
        _clearCareCoordinationContextStore();
        // Set Sentry user context for error tracking
        setSentryUser({ id: user.id, email: user.email });

        return set({
          user,
          token: token || null,
          isAuthenticated: true,
          ..._roleFlags(user.role),
        });
      },
      logout: async () => {
        const controller = new AbortController();
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        try {
          // Call API logout to clear server-side session and cookies
          // Use a shorter timeout for logout to avoid hanging
          timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

          await authAPI.logout(controller.signal);
        } catch (error) {
          // Use secure error handling for logout errors
          const sanitizedError = secureErrorHandler.handleApiError(
            error,
            "AuthStore.logout"
          );
          console.warn("API logout failed:", sanitizedError.message);
          // Continue with client-side cleanup even if API fails
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
          // CRITICAL: Clear all localStorage FIRST before any state updates
          // This prevents Zustand persist middleware from rehydrating stale state

          // Clear provider search state, including location and user selections.
          try {
            useProviderStore.getState().reset();
          } catch (error) {
            console.warn("Failed to reset provider store user data:", error);
          }

          // Clear all auth-related localStorage keys
          localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
          localStorage.removeItem(TOKEN_STORAGE_KEYS.TOKEN);
          localStorage.removeItem(TOKEN_STORAGE_KEYS.AUTH_TOKEN);

          // Clear clinical PHI from memory and any leftover patient_* localStorage keys
          try {
            offlineStorage.clearAllData();
          } catch {
            // PHI clear must not break logout
          }

          // Selective auth/CSRF session keys only — never sessionStorage.clear()
          clearSetupChecklistStorageOnLogout();
          clearAuthSessionStorage();
          clearStashedAgencyInvitationToken();

          // Clear secure token storage
          secureTokenManager.clearToken();

          // Clear Sentry user context
          setSentryUser(null);
          // Clear product analytics identity (never throw)
          try {
            productAnalytics.reset();
          } catch {
            // analytics must not break logout
          }
          try {
            clearDashboardCompareLastOpenedStorage();
          } catch {
            // storage clear must not break logout
          }

          // Clear auth state AFTER localStorage is cleared
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isProvider: false,
            isProviderPortalUser: false,
            isProviderAccountOwner: false,
            isSocialWorker: false,
            isPatient: false,
            isAdmin: false,
            isPlatformAdmin: false,
          });
          _clearOrgContextStore();
          _clearCommercialEntitlementStore();
          _clearCareCoordinationContextStore();

          // Persist cleared state synchronously so reload/rehydration never sees stale auth.
          // Zustand persist may write async; writing ourselves guarantees storage is correct before redirect.
          // Guard for browser (Azure prod runs in client; guards help SSR/tests).
          if (
            typeof window !== "undefined" &&
            typeof localStorage !== "undefined"
          ) {
            const clearedPersistedState = {
              state: { user: null, isAuthenticated: false },
              version: AUTH_PERSIST_VERSION,
            };
            try {
              localStorage.setItem(
                STORAGE_KEYS.AUTH_STORAGE,
                JSON.stringify(clearedPersistedState)
              );
            } catch {
              localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
            }
            // Full URL so redirect is correct in Azure/prod (same origin, no base-path issues)
            window.location.href = `${window.location.origin}/login`;
          }
        }
      },
      updateUser: (user: User) =>
        set((state) => ({
          ...state,
          user,
          ..._roleFlags(user.role),
        })),
      updateToken: (token: string | null) =>
        set({
          token,
        }),
      clearAuth: () => {
        // Clear secure token storage
        secureTokenManager.clearToken();
        // Clear product analytics identity (never throw)
        try {
          productAnalytics.reset();
        } catch {
          // analytics must not break clearAuth
        }
        try {
          clearDashboardCompareLastOpenedStorage();
        } catch {
          // storage clear must not break clearAuth
        }
        // Clear Sentry user context (same as logout)
        setSentryUser(null);

        // Clear provider search state, including location and user selections.
        try {
          useProviderStore.getState().reset();
        } catch (error) {
          console.warn("Failed to reset provider store user data:", error);
        }

        // Clear client-side state and localStorage
        // Use constants for consistent key names
        localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
        // Also clear legacy token keys for migration
        localStorage.removeItem(TOKEN_STORAGE_KEYS.TOKEN);
        localStorage.removeItem(TOKEN_STORAGE_KEYS.AUTH_TOKEN);

        // Clear clinical PHI from memory and any leftover patient_* localStorage keys
        try {
          offlineStorage.clearAllData();
        } catch {
          // PHI clear must not break clearAuth
        }

        // Selective auth/CSRF session keys only — never sessionStorage.clear()
        clearAuthSessionStorage();

        // Clear auth state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isProvider: false,
          isProviderPortalUser: false,
          isProviderAccountOwner: false,
          isSocialWorker: false,
          isPatient: false,
          isAdmin: false,
          isPlatformAdmin: false,
        });
        _clearOrgContextStore();
        _clearCommercialEntitlementStore();
        _clearCareCoordinationContextStore();

        // Same as logout: write cleared persist state so reload never rehydrates as logged-in
        if (
          typeof window !== "undefined" &&
          typeof localStorage !== "undefined"
        ) {
          try {
            localStorage.setItem(
              STORAGE_KEYS.AUTH_STORAGE,
              JSON.stringify({
                state: { user: null, isAuthenticated: false },
                version: AUTH_PERSIST_VERSION,
              })
            );
          } catch {
            localStorage.removeItem(STORAGE_KEYS.AUTH_STORAGE);
          }
        }
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_STORAGE,
      version: AUTH_PERSIST_VERSION,
      partialize: (state) => ({
        user: toPersistedUser(state.user),
        isAuthenticated: state.isAuthenticated,
      }),
      migrate: (persistedState: unknown, version: number) => {
        if (!persistedState || typeof persistedState !== "object") {
          return persistedState as never;
        }
        const state = persistedState as {
          user?: User | null;
          isAuthenticated?: boolean;
        };
        // Always strip email/name from any persisted user blob (every version).
        if (state.user) {
          state.user = {
            ...(toPersistedUser(state.user) as User),
            email: "",
            first_name: "",
            last_name: "",
          };
        }
        void version; // version used by zustand; always strip regardless
        return state as never;
      },
      // Role-flag booleans are not persisted (only user + isAuthenticated are).
      // Recompute them from user.role every time the store rehydrates from
      // localStorage so that canViewProviderStats and other role checks work
      // correctly after a page reload without requiring a new login.
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          // Hard strip any PII that slipped into storage or memory on rehydrate
          const minimal = toPersistedUser(state.user);
          state.user = {
            id: minimal?.id ?? state.user.id,
            role: (minimal?.role ?? state.user.role) as User["role"],
            is_active: minimal?.is_active ?? state.user.is_active,
            is_verified: minimal?.is_verified ?? state.user.is_verified,
            email: "",
            first_name: "",
            last_name: "",
          };
          const flags = _roleFlags(state.user.role);
          state.isProvider = flags.isProvider;
          state.isProviderPortalUser = flags.isProviderPortalUser;
          state.isProviderAccountOwner = flags.isProviderAccountOwner;
          state.isSocialWorker = flags.isSocialWorker;
          state.isPatient = flags.isPatient;
          state.isAdmin = flags.isAdmin;
          state.isPlatformAdmin = flags.isPlatformAdmin;
        }
      },
    }
  )
);

// Register auth store getter to break circular dependency with secureTokenHandler
// This allows secureTokenHandler to access auth store without importing it directly
// The registration happens after both modules are loaded, breaking the cycle
try {
  // Use dynamic import to avoid circular dependency at module load time
  import("../utils/secureTokenHandler")
    .then((module) => {
      if (module.registerAuthStoreGetter) {
        module.registerAuthStoreGetter(() => {
          const state = useAuthStore.getState();
          // Map AuthState to AuthStoreState format expected by secureTokenHandler
          return {
            user: state.user ? { id: state.user.id } : undefined,
            isAuthenticated: state.isAuthenticated,
            clearAuth: state.clearAuth,
            logout: state.logout,
          };
        });
      }
    })
    .catch(() => {
      // If secureTokenHandler is not loaded yet, that's okay
      // It will use the fallback mechanism
    });
} catch {
  // Ignore errors during module load
}
